import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import { deleteCounterpartyApi, listCounterpartiesApi, updateCounterpartyApi } from "../api/financeiroApi";
import type { FinCounterparty, FinPaginated } from "../api/finTypes";
import { formatDocument } from "../utils/fin";
import CounterpartyFormModal from "./CounterpartyFormModal";

type Props = { token: string; canWrite: boolean };

const PAGE_SIZE = 20;

/** Fornecedores e clientes: busca por nome ou CPF/CNPJ. */
export default function CounterpartiesSettings({ token, canWrite }: Props) {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FinPaginated<FinCounterparty> | null>(null);
  const [editing, setEditing] = useState<FinCounterparty | "new" | null>(null);
  const [removing, setRemoving] = useState<FinCounterparty | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listCounterpartiesApi(token, { q: query || undefined, page, limit: PAGE_SIZE, includeInactive: true })
      .then(setData)
      .catch((error: Error) => toast.error(error.message));
  }, [token, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (cp: FinCounterparty) => {
    try {
      await updateCounterpartyApi(token, cp.id, { isActive: !cp.isActive });
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o cadastro.");
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await deleteCounterpartyApi(token, removing.id);
      toast.success("Cadastro excluído.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o cadastro.");
    } finally {
      setRemoving(null);
      setBusy(false);
    }
  };

  return (
    <div className="dpeSection">
      <div className="dpControls">
        <input
          className="ecoInput"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Buscar por nome ou CPF/CNPJ"
          aria-label="Buscar"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              setQuery(q.trim());
            }
          }}
          onBlur={() => {
            if (q.trim() !== query) {
              setPage(1);
              setQuery(q.trim());
            }
          }}
        />
        {canWrite ? (
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setEditing("new")}>
            Novo cadastro
          </button>
        ) : null}
      </div>

      <section className="ecoCard">
        <div className="ecoTableWrap">
          <table className="ecoTable finTable">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF / CNPJ</th>
                <th>Contato</th>
                <th>PIX</th>
                {canWrite ? <th className="ecoTable__num">Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr>
                  <td colSpan={5} className="ecoEmpty">
                    Carregando...
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ecoEmpty">
                    {query ? "Nada encontrado." : "Nenhum fornecedor ou cliente cadastrado."}
                  </td>
                </tr>
              ) : (
                data.items.map((cp) => (
                  <tr key={cp.id} className={cp.isActive ? "" : "finRow--canceled"}>
                    <td>
                      <strong>{cp.name}</strong>
                      {!cp.isActive ? <div className="finDesc__sub">Inativo</div> : null}
                    </td>
                    <td className="ecoTable__muted finNowrap">{formatDocument(cp.document)}</td>
                    <td className="ecoTable__muted">
                      {cp.email || cp.phone ? (
                        <span className="finDesc">
                          {cp.email ? <span style={{ overflowWrap: "anywhere" }}>{cp.email}</span> : null}
                          {cp.phone ? <span className="finNowrap">{cp.phone}</span> : null}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="ecoTable__muted">{cp.pixKey || "—"}</td>
                    {canWrite ? (
                      <td className="ecoTable__num">
                        <div className="dpTable__actions" style={{ justifyContent: "flex-end" }}>
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setEditing(cp)}>
                            Editar
                          </button>
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => toggleActive(cp)}>
                            {cp.isActive ? "Inativar" : "Reativar"}
                          </button>
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--sm ecoBtn--danger"
                            onClick={() => setRemoving(cp)}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.total > PAGE_SIZE ? (
          <EcoPagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />
        ) : null}
      </section>

      {editing ? (
        <CounterpartyFormModal
          token={token}
          counterparty={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir cadastro?"
        message={`"${removing?.name ?? ""}" será excluído. Só é possível sem lançamentos; com histórico, inative.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={busy}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
